<template>
  <div
    style="gap: 1rem"
    class="f-column"
    :class="[{ gw_loading: loading }]"
  >
    <div class="f-column">
      <GwTitleTooltip
        :title="$t(`solfigr.icm.remaining_stacks`)"
        :tooltip="$t(`solfigr.icm.remaining_stacks_tooltip`)"
        style="margin: 0 0 0.25rem 0"
      />
      <GwBtns
        :options="['sdt_lognormal', 'sdt_manual']"
        class="gwbtns_horizontal_grey gw_loading_text mr-auto"
        style="
          margin-right: 0.5rem;
          border-radius: 4px;
          background: var(--clr-black-1-3);
          --gwbtn-clr-back: var(--clr-black-1-3);
          --gwbtn-clr-active-white: var(--clr-black-3);
          --gwbtn-size: 1rem;
          --gwbtn-height: 1.875rem;
          --gwbtns-padding: 2px;
        "
        :value="icmPlayersAndStacks.stacks_distribution_type"
        tstPrefix="stacks_distribution_type"
        @click="(_e, item) => updateValue('stacks_distribution_type', item)"
      >
        <template #default="{ item }">
          {{ $t(`solfigr.icm.${item}`) }}
        </template>
      </GwBtns>
    </div>
    <div
      v-if="icmPlayersAndStacks.stacks_distribution_type === 'sdt_lognormal'"
      class="f-column"
      style="gap: 1rem"
    >
      <div
        class="d-flex"
        style="gap: 1rem"
      >
        <div
          class="f-column"
          data-tst="num_of_players_remaining"
        >
          <GwTitleTooltip
            :title="$t(`solfigr.icm.num_of_players_remaining`)"
            :tooltip="$t(`solfigr.icm.num_of_players_remaining_tooltip`)"
            style="margin: 0 0 0.25rem 0"
          />
          <NumberInput
            :min="conf.num_of_players_remaining.min"
            :max="maxPlayers || conf.num_of_players_remaining.max"
            :step="conf.num_of_players_remaining.step"
            :decimals="conf.num_of_players_remaining.decimals"
            :inputmode="conf.num_of_players_remaining.inputmode"
            :hasMinus="false"
            :hasPlus="false"
            :hasWheelScroll="false"
            style="--input-width: 6rem"
            :value="icmPlayersAndStacks.num_of_players_remaining"
            @input="value => updateValue('num_of_players_remaining', value)"
          />
        </div>
        <div
          class="f-column"
          :data-tst="
            conf.num_of_players_remaining.min === icmPlayersAndStacks.num_of_players_remaining
              ? `avg_stack_disabled`
              : `avg_stack`
          "
        >
          <GwTitleTooltip
            :title="$t(`solfigr.icm.avg_stack`)"
            :tooltip="$t(`solfigr.icm.avg_stack_tooltip`)"
            style="margin: 0 0 0.25rem 0"
          />
          <NumberInput
            :min="conf.avg_stack.min"
            :max="conf.avg_stack.max"
            :step="conf.avg_stack.step"
            :decimals="conf.avg_stack.decimals"
            :inputmode="conf.avg_stack.inputmode"
            :hasMinus="false"
            :hasPlus="false"
            :hasWheelScroll="false"
            style="--input-width: 6rem"
            :value="icmPlayersAndStacks.avg_stack"
            :style="
              conf.num_of_players_remaining.min === icmPlayersAndStacks.num_of_players_remaining
                ? `pointer-events: none; --input-color: var(--clr-grey-2);            `
                : ``
            "
            @input="
              value => {
                if (
                  conf.num_of_players_remaining.min === icmPlayersAndStacks.num_of_players_remaining
                ) {
                  return
                }
                updateValue('avg_stack', value)
              }
            "
          />
        </div>
      </div>
      <SolfigrIcmStacksDistributionChart
        :remainingPlayers="icmPlayersAndStacks.num_of_players_remaining"
        :avgStack="icmPlayersAndStacks.avg_stack"
        :stackIP="icmHandDetails && icmHandDetails.stack_IP"
        :stackOOP="icmHandDetails && icmHandDetails.stack_OOP"
        :players
        :logNormalDeviation="icmPlayersAndStacks.lognormal_deviation"
        @update:values="values => updateValues(values)"
      />
    </div>

    <div
      v-if="hasStacksTable && icmPlayersAndStacks.stacks_distribution_type === 'sdt_manual'"
      class="f-column flex-1"
      style="gap: 0.5rem"
    >
      <SolfigrIcmRemainingStacksTable
        :items="icmPlayersAndStacks.stacks_manual"
        :max="maxPlayers || conf.num_of_players_remaining.max"
        :min="minPlayers || 0"
        :stackIP="icmHandDetails && icmHandDetails.stack_IP"
        :stackOOP="icmHandDetails && icmHandDetails.stack_OOP"
        :players
        @update:items="value => updateValue('stacks_manual', value)"
      />
    </div>

    <div
      v-if="hasBountyAvg"
      class="f-column"
      data-tst="bounty_avg"
    >
      <GwTitleTooltip
        :title="$t(`solfigr.icm.avg_bounty`)"
        :tooltip="$t(`solfigr.icm.avg_bounty_tooltip`)"
        style="margin: 0 0 0.25rem 0"
      />

      <div class="position-relative mr-auto f-center">
        <NumberInput
          :min="conf.avg_bounty.min"
          :max="conf.avg_bounty.max"
          :step="conf.avg_bounty.step"
          :decimals="conf.avg_bounty.decimals"
          :inputmode="conf.avg_bounty.inputmode"
          :hasMinus="false"
          :hasPlus="false"
          :hasWheelScroll="false"
          style="--input-width: 8rem"
          :value="icmPlayersAndStacks.avg_bounty"
          @input="value => updateValue('avg_bounty', value)"
        />
        <div
          class="clr-grey weight_500 text-medium"
          style="position: absolute; right: 0.5rem; z-index: 8"
        >
          {{ conf.avg_bounty.unit }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GwBtns from '@/components/lib/GwBtns'
import GwTitleTooltip from '@/components/Dialogs/UserSettingsDialog/GwTitleTooltip'
import NumberInput from '@/components/lib/NumberInput'
import { getSolfigrFields } from '@/store/solfigr/solfigr_helpers'
import float_mix from '@/mixins/float_mix'
import SolfigrIcmStacksDistributionChart from '@/components/ConfigureSolution/SolfigrIcmStacksDistributionChart'
import SolfigrIcmRemainingStacksTable from '@/components/ConfigureSolution/SolfigrIcmRemainingStacksTable'

export default {
  name: 'SolfigrIcmOtherStacks',
  components: {
    SolfigrIcmRemainingStacksTable,
    SolfigrIcmStacksDistributionChart,
    GwBtns,
    GwTitleTooltip,
    NumberInput,
  },
  mixins: [float_mix],
  props: {
    namespace: { String, required: true },
    loading: Boolean,
    icmPlayersAndStacks: Object,
    icmHandDetails: Object,
    players: Array,
    hasStacksTable: { type: Boolean, default: true },
    hasBountyAvg: { type: Boolean, default: true },
    maxPlayers: Number,
    minPlayers: Number,
  },
  emits: ['update:icmPlayersAndStacks'],
  data() {
    this.conf = getSolfigrFields()
    return {}
  },
  methods: {
    updateValue(key, value) {
      this.$emit('update:icmPlayersAndStacks', {
        ...this.icmPlayersAndStacks,
        [key]: value,
      })
    },
    updateValues(values) {
      this.$emit('update:icmPlayersAndStacks', {
        ...this.icmPlayersAndStacks,
        ...values,
      })
    },
  },
}
</script>
